import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import { useTradingOfferOperationDetail } from './context/TradingOfferOperationDetailContext';
import getOfferReference from '../../utils/getOfferReference';

import TradingOfferOperationDetailHeader from './components/TradingOfferOperationDetailHeader';
import TradingOfferOperationDetailStatusBanner from './components/TradingOfferOperationDetailStatusBanner';
import TradingOfferOperationDetailSummaryCard from './components/TradingOfferOperationDetailSummaryCard';
import TradingOfferOperationDetailTimeline from './components/TradingOfferOperationDetailTimeline';
import TradingOfferOperationDetailPartiesCard from './components/TradingOfferOperationDetailPartiesCard';
import TradingOfferOperationDetailPaymentCard from './components/TradingOfferOperationDetailPaymentCard';
import TradingOfferOperationDetailDocumentation from './components/TradingOfferOperationDetailDocumentation';
import TradingOfferOperationDetailComments from './components/TradingOfferOperationDetailComments';
import TradingOfferOperationDetailActivityHistory from './components/TradingOfferOperationDetailActivityHistory';

const formatDate = (
  date: Date | string | undefined,
  pattern = 'dd MMM yyyy'
) => {
  if (!date) return '-';
  return format(new Date(date), pattern, { locale: es });
};

const TradingOfferOperationDetail: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useTradingOfferOperationDetail();

  const formattedRequestDate = useMemo(
    () => formatDate(data.operation?.requestDate),
    [data.operation]
  );

  const sellerName = useMemo(() => {
    if (data.tradingOffer?.isSellerNameHidden) return t('HiddenName');
    if (!data.operation?.seller) return '';
    if (typeof data.operation.seller === 'string') return '';
    return data.operation.seller.name || '';
  }, [data.operation, data.tradingOffer?.isSellerNameHidden, t]);

  const shareClassName = useMemo(() => {
    // Prefer operation.shareClass (populated in API) over tradingOffer.shareClass
    const shareClass =
      data.operation?.shareClass ?? data.tradingOffer?.shareClass;
    if (!shareClass || typeof shareClass === 'string') return '';
    return (shareClass as { name?: string })?.name || '';
  }, [data.operation, data.tradingOffer]);

  const offerReference = useMemo(
    () => getOfferReference(data.tradingOffer),
    [data.tradingOffer]
  );

  const buyerDocumentationEntries = useMemo(
    () =>
      (data.operation?.buyerDocuments || []).map((document) => ({
        document,
        isFromOffer: false as const,
      })),
    [data.operation?.buyerDocuments]
  );

  const sellerDocumentationEntries = useMemo(() => {
    const offerDocs = (data.tradingOffer?.documents || []).map((doc) => ({
      document: doc,
      isFromOffer: true as const,
    }));
    const opSellerDocs = (data.operation?.sellerDocuments || []).map(
      (document) => ({
        document,
        isFromOffer: false as const,
      })
    );
    return [...offerDocs, ...opSellerDocs];
  }, [data.tradingOffer?.documents, data.operation?.sellerDocuments]);

  if (!data.operation && !data.isLoading) {
    return (
      <Page id="trading-offer-operation-detail-page">
        <div className="nk-block text-center py-5">
          <em className="icon ni ni-alert-circle fs-1 text-warning mb-3 d-block" />
          <h5>{t('OperationNotFound')}</h5>
          <p className="text-soft">{t('OperationNotFoundDescription')}</p>
          <button
            type="button"
            className="btn btn-primary mt-2"
            onClick={actions.handleGoBack}
          >
            {t('Back')}
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page id="trading-offer-operation-detail-page">
      <TradingOfferOperationDetailHeader
        tradingOffer={data.tradingOffer}
        operationReference={data.operationReference}
        operationStatus={data.operationStatus}
        formattedRequestDate={formattedRequestDate}
        sellerName={sellerName}
        isAdmin={data.isAdmin}
        societyId={data.societyId}
        societyName={data.societyName}
        onUpdateStatus={actions.handleUpdateStatus}
      />

      <TradingOfferOperationDetailStatusBanner
        operationStatus={data.operationStatus}
        sharesCount={data.operation?.sharesCount || 0}
        shareClassName={shareClassName}
      />

      <div className="nk-block">
        <div className="row g-gs">
          {/* Left Column */}
          <div className="col-lg-7">
            <TradingOfferOperationDetailSummaryCard
              operation={data.operation}
              tradingOffer={data.tradingOffer}
              operationReference={data.operationReference}
              offerReference={offerReference}
              shareClassName={shareClassName}
              formattedRequestDate={formattedRequestDate}
              onGoToOffer={actions.handleGoToOffer}
            />

            <TradingOfferOperationDetailTimeline
              steps={data.timelineSteps}
            />
          </div>

          {/* Right Column */}
          <div className="col-lg-5">
            <TradingOfferOperationDetailPartiesCard
              buyerInfo={data.buyerInfo}
              sellerInfo={data.sellerInfo}
            />

            <TradingOfferOperationDetailPaymentCard />

            <TradingOfferOperationDetailDocumentation
              buyerDocuments={buyerDocumentationEntries}
              sellerDocuments={sellerDocumentationEntries}
              sellerName={sellerName}
              equityMarketPurchaseContract={
                data.operation?.equityMarketPurchaseContract
              }
            />
          </div>
        </div>
      </div>

      <TradingOfferOperationDetailComments />

      <TradingOfferOperationDetailActivityHistory
        statusHistory={data.operation?.statusHistory || []}
      />

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default TradingOfferOperationDetail;
